import { Link, Outlet, createFileRoute, redirect, useParams, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { usePageBar } from "../../components/shell/page-bar";
import { transportSummaryOptions } from "../../lib/api/transport";
import { useDefaultGamesId } from "../../lib/competition/games";
import { canSeeFleet, canSeeTransport, transportView } from "../../lib/transport/access";
import { attentionCount, deskSummary } from "../../lib/transport/view";

/**
 * Transportation (M10, PLAN §4) — the layout: her tab row over the page.
 *   dispatch desk  Overview · Requests · Drivers & vehicles
 *   provider       Trips · Drivers & vehicles (its own fleet, read-only)
 *   NOC            one page ("Your trips"), no tabs
 * A trip (`requests/$requestId`) and a driver's week (`drivers/$driverId`)
 * are full pages with the back arrow instead of the tabs.
 *
 * Who: `canSeeTransport` (admins, `transport.dispatch`, `transport.request`,
 * a provider login). A courtesy, not the control — the API scopes every
 * row in SQL and checks every write.
 */
export const Route = createFileRoute("/_app/logistics/transportation")({
	validateSearch: z.object({ games_id: z.string().uuid().optional().catch(undefined) }),
	beforeLoad: ({ context }) => {
		if (!canSeeTransport(context.session)) throw redirect({ to: "/" });
	},
	staticData: { contentTop: "tabbed" },
	component: TransportLayout,
});

type Tab = { key: string; to: "/logistics/transportation" | "/logistics/transportation/requests" | "/logistics/transportation/fleet"; badge?: number };

function TransportLayout() {
	const { t } = useTranslation("transport");
	const { t: tn } = useTranslation("nav");
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const params = useParams({ strict: false }) as { requestId?: string; driverId?: string };
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const view = transportView(session);
	const gamesId = useDefaultGamesId(search.games_id);
	// The Requests tab's red count: trips a delay left without a vehicle (X12). Shares the Overview's query on today.
	const summary = useQuery({ ...transportSummaryOptions(gamesId ?? ""), enabled: !!gamesId && view === "dispatch" });
	const desk = deskSummary(summary.data);
	const redispatch = desk ? attentionCount(desk, "REDISPATCH") : undefined;
	const detail = params.requestId !== undefined || params.driverId !== undefined;

	// Her heading in either menu: "Transportation / Logistics & Hospitality".
	usePageBar({ subtitle: tn("sections.logistics") });

	if (detail) return <Outlet />;

	const tabs: Tab[] =
		view === "dispatch"
			? [
					{ key: "overview", to: "/logistics/transportation" },
					{ key: "requests", to: "/logistics/transportation/requests", badge: redispatch },
					{ key: "fleet", to: "/logistics/transportation/fleet" },
				]
			: view === "provider"
				? [
						{ key: "trips", to: "/logistics/transportation" },
						...(canSeeFleet(session) ? [{ key: "fleet", to: "/logistics/transportation/fleet" } as const] : []),
					]
				: [];
	const current = (to: Tab["to"]) => (to === "/logistics/transportation" ? pathname === to || pathname === `${to}/` : pathname.startsWith(to));

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			{tabs.length > 0 && (
				<nav aria-label={t("tabs.label")} className="-mt-[0.25rem] flex gap-[0.25rem] overflow-x-auto border-b border-gms-line [scrollbar-width:none]">
					{tabs.map((tab) => (
						<Link
							key={tab.key}
							to={tab.to}
							search={(s: { games_id?: string }) => ({ games_id: s.games_id })}
							aria-current={current(tab.to) ? "page" : undefined}
							className={`relative inline-flex h-[2.75rem] shrink-0 items-center gap-[0.375rem] px-[0.75rem] text-[0.875rem] whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-gms-navy/40 ${
								current(tab.to)
									? "font-semibold text-gms-navy after:absolute after:inset-x-[0.5rem] after:-bottom-px after:h-[3px] after:rounded-t-[3px] after:bg-gms-red"
									: "font-medium text-gms-ink-2 hover:text-gms-navy"
							}`}
						>
							{t(`tabs.${tab.key as "overview"}`)}
							{!!tab.badge && tab.badge > 0 && (
								<span
									title={t("tabs.redispatchBadge", { count: tab.badge })}
									className="inline-grid h-[1.125rem] min-w-[1.25rem] place-items-center rounded-full bg-gms-red px-[0.3125rem] text-[0.6875rem] font-bold text-white tabular-nums"
								>
									{tab.badge}
								</span>
							)}
						</Link>
					))}
				</nav>
			)}
			<Outlet />
		</div>
	);
}
